import { FC, MouseEvent, useMemo, useCallback } from "react";
import { Box } from "@mui/material";

import { useAsu } from "@framework/provider-asu";

import { IStationScheme, IStationSchemeObject, ObjectClassParam } from "../../../../interfaces";
import { useAlarmPaletteResolver } from "../../../../../../../../../../hooks/use-alarm-color";
import { useImageSize } from "../../../../../../../../../../utils";
import { openPopupWithParams } from "./utils";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";
import { SYNC_DELAY } from "@framework/constants";
import { ITrainControlsResponse } from "../../../../../../components/train/interfaces";
import { TiAnalogDisplay } from "../../../../../../components/train/components/ti-analog-display";
import { TiLabel } from "../../../../../../components/train/components/ti-label";
import { TiSchemeButton } from "../../../../../../components/train/components/ti-scheme-button";
import { TiLedRound } from "../../../../../../components/train/components/ti-led-round";
import { TiSevenSegmentAnalog } from "../../../../../../components/train/components/ti-seven-segment-analog";
import { TiThermometer } from "../../../../../../components/train/components/ti-thermometer";
import { TiAngularGauge } from "../../../../../../components/train/components/ti-angular-gauge";
import { TiLinearGauge } from "../../../../../../components/train/components/ti-linear-gauge";
import { formatSchemeControlMeasurementValue } from "../../../../../../utils/measurement-format";

const shouldFlashUnacknowledged = (event: number, flashUnack: boolean) => flashUnack && event > 0;

function stationSchemeObjectDomId(obj: IStationSchemeObject): string {
  const { ObjectClass, SchemeIdBindObject, left, top, right, bottom } = obj;
  return `object__${ObjectClass}__${SchemeIdBindObject}__${left + top + right + bottom}`;
}

const SCHEME_AREA_OVERLAY_CLASS = "station-scheme-area-overlay";

const makeSvgStyles = (suffix: string) => `
  .${SCHEME_AREA_OVERLAY_CLASS} .scheme-area-object { cursor: pointer; }
  .${SCHEME_AREA_OVERLAY_CLASS} .flashing-unacknowledged,
  .${SCHEME_AREA_OVERLAY_CLASS} .flashing-unacknowledged path,
  .${SCHEME_AREA_OVERLAY_CLASS} .flashing-unacknowledged rect {
    animation: flashUnacknowledged${suffix} 1s steps(1, end) ${SYNC_DELAY} infinite;
  }
  @keyframes flashUnacknowledged${suffix} {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
  .${SCHEME_AREA_OVERLAY_CLASS} .flashing-fault-rect {
    animation: flashFault${suffix} 1s steps(1, end) ${SYNC_DELAY} infinite;
  }
  @keyframes flashFault${suffix} {
    0%, 100% { border-color: red; }
    50% { border-color: transparent; }
  }
  .${SCHEME_AREA_OVERLAY_CLASS} .scheme-object__title {
    font-family: "Raleway", "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    text-anchor: middle;
    dominant-baseline: middle;
  }
`;

function stationSchemePctCoord(v: number, total: number): string {
  if (total <= 0) return "0%";
  return `${(v / total) * 100}%`;
}

function stationObjectOverlayBox(
  obj: IStationSchemeObject,
  natW: number,
  natH: number,
): { left: string; top: string; width: string; height: string } {
  const w = Math.max(0, obj.right - obj.left);
  const h = Math.max(0, obj.bottom - obj.top);
  return {
    left: stationSchemePctCoord(obj.left, natW),
    top: stationSchemePctCoord(obj.top, natH),
    width: stationSchemePctCoord(w, natW),
    height: stationSchemePctCoord(h, natH),
  };
}

function mergeStationCtrlProps(obj: IStationSchemeObject): Record<string, unknown> {
  const p = obj.ctrlProps;
  if (p != null && typeof p === "object" && !Array.isArray(p)) {
    return { ...p };
  }
  return {};
}

function isStationLabelObject(obj: IStationSchemeObject): boolean {
  return obj.ObjectClass === ObjectClassParam.CAlarmStatusObject && obj.ctrlComponentName === "TiLabel";
}

function schemeButtonCaption(obj: IStationSchemeObject): string | null {
  const raw = mergeStationCtrlProps(obj);
  const cap = raw.Caption ?? raw.caption;
  if (cap == null) return null;
  const text = String(cap).trim();
  return text === "" ? null : text;
}

function isDefaultSchemeButtonCaption(obj: IStationSchemeObject): boolean {
  const cap = schemeButtonCaption(obj);
  return cap == null || cap === "iLabel";
}

function styledSchemeButtonComponentName(obj: IStationSchemeObject): string {
  return obj.ctrlComponentName === "TiLabel" ? "TiLabel" : "TiSchemeButton";
}

function stationObjectTrainComponentName(obj: IStationSchemeObject): string {
  if (isStationLabelObject(obj)) {
    return "TiLabel";
  }

  switch (obj.ObjectClass) {
    case ObjectClassParam.CAnalogDisplayObject:
      return "TiAnalogDisplay";
    case ObjectClassParam.CThermometerObject:
      return "TiThermometer";
    case ObjectClassParam.CAngularGaugeObject:
      return "TiAngularGauge";
    case ObjectClassParam.CLinearGaugeObject:
      return "TiLinearGauge";
    case ObjectClassParam.C7SegmentObject:
      return "TiSevenSegmentAnalog";
    case ObjectClassParam.CLedRoundObject:
    case ObjectClassParam.CLedRoundExtObject:
      return "TiLedRound";
    case ObjectClassParam.CSchemeButtonObject:
      return "TiSchemeButton";
    default:
      return "TiUnsupported";
  }
}

function buildTrainControlFromStationObject(
  obj: IStationSchemeObject,
  idx: number,
  options?: {
    stationSchemeStatusIndicator?: boolean;
    resolvedAlarmStatus?: number;
    omitTrainAndTopLevelCaption?: boolean;
    styledSchemeButton?: boolean;
    dataFormat?: IUser["interfaceSettings"]["dataFormat"] | null;
  },
): ITrainControlsResponse {
  const raw = mergeStationCtrlProps(obj);
  const styledSchemeButton = options?.styledSchemeButton === true;
  const componentName =
    obj.ObjectClass === ObjectClassParam.CSchemeButtonObject && styledSchemeButton
      ? styledSchemeButtonComponentName(obj)
      : stationObjectTrainComponentName(obj);
  const isButton =
    componentName === "TiSchemeButton" ||
    (obj.ObjectClass === ObjectClassParam.CSchemeButtonObject && styledSchemeButton);
  const stationSchemeStatusIndicator =
    obj.ObjectClass === ObjectClassParam.CSchemeButtonObject && styledSchemeButton
      ? false
      : options?.stationSchemeStatusIndicator === true;
  const omitAbbrev = options?.omitTrainAndTopLevelCaption === true;
  const captionText = schemeButtonCaption(obj);
  const valueForButton = stationSchemeStatusIndicator
    ? ""
    : captionText != null
      ? captionText
      : omitAbbrev
        ? ""
        : obj.TrainName != null
          ? String(obj.TrainName)
          : obj.TopLevelName != null
            ? String(obj.TopLevelName)
            : "";
  const valueForControl = formatSchemeControlMeasurementValue({
    bindObjectType: obj.SchemeBindObjectType,
    componentName,
    rawValue: obj.rawValue,
    settings: options?.dataFormat,
    sourceValue: obj.value != null ? String(obj.value) : null,
    valueUnits: obj.valueUnits,
  });

  const props = {
    ...raw,
    Left: 0,
    Top: 0,
    Width: "100%",
    Height: "100%",
  } as unknown as ITrainControlsResponse["props"];

  return {
    value: isButton ? valueForButton : valueForControl,
    ldd: obj.ldd ?? null,
    rawValue: obj.rawValue ?? null,
    valueUnits: obj.valueUnits ?? null,
    props,
    idSchemeObject: (idx + 1) * 100000 + (obj.SchemeIdBindObject ?? 0) + (obj.left | 0),
    componentName,
    alarmStatus: options?.resolvedAlarmStatus ?? obj.alarmStatus ?? 0,
    unacknowledgedEvent: obj.unacknowledgedEvent ?? 0,
    bindObjectType: obj.SchemeBindObjectType,
    idBindObject: obj.SchemeIdBindObject,
    objectClass: obj.ObjectClass,
    stationSchemeStatusIndicator,
  };
}

export interface ISchemeArea {
  scheme: IStationScheme;
  objects: IStationSchemeObject[];
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
  onShowDefects?: (trainId: number) => void;
}

export const SchemeArea: FC<ISchemeArea> = props => {
  const { scheme, objects, flashUnacknowledgedAlarms, flashFaultAlarms, onShowDefects } = props;
  const user = useUser<IUser>();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const { selectStation, selectTrain, getStationAlarmStatus, getTrainAlarmStatus } = useAsu();
  const getStationObjectKey = ({ ObjectClass, SchemeIdBindObject, left, top, right, bottom }: IStationSchemeObject) =>
    `${ObjectClass}_${SchemeIdBindObject}_${left}_${top}_${right}_${bottom}`;

  const schemeInNewWindow = user.profile.interfaceSettings.openTrainSchemeInNewWindow;
  const dataFormat = user.profile.interfaceSettings.dataFormat;

  const schemeStatusIndicatorKeySet = useMemo(() => {
    const keyOf = ({ ObjectClass, SchemeIdBindObject, left, top, right, bottom }: IStationSchemeObject) =>
      `${ObjectClass}_${SchemeIdBindObject}_${left}_${top}_${right}_${bottom}`;
    const buttons = objects.filter(
      o => o.ObjectClass === ObjectClassParam.CSchemeButtonObject && isDefaultSchemeButtonCaption(o),
    );
    const minTopByTrainBind = new Map<number, number>();
    for (const obj of buttons) {
      if (obj.SchemeBindObjectType !== 2 || obj.TrainName == null) continue;
      const id = obj.SchemeIdBindObject ?? 0;
      const prev = minTopByTrainBind.get(id);
      if (prev === undefined || obj.top < prev) minTopByTrainBind.set(id, obj.top);
    }
    const set = new Set<string>();
    for (const obj of buttons) {
      if (obj.SchemeBindObjectType === 1) {
        set.add(keyOf(obj));
        continue;
      }
      if (obj.SchemeBindObjectType === 2 && obj.TrainName != null) {
        const minT = minTopByTrainBind.get(obj.SchemeIdBindObject ?? 0);
        if (minT !== undefined && obj.top === minT) set.add(keyOf(obj));
      }
    }
    return set;
  }, [objects]);

  const {
    foregroundControlObjects,
    buttonObjects,
    styledButtonObjects,
    alarmStatusObjects,
    alarmStatusObjectsWithFault,
    buttonObjectsWithFault,
  } = useMemo(() => {
    const foregroundControlObjects: IStationSchemeObject[] = [];
    const buttonObjects: IStationSchemeObject[] = [];
    const styledButtonObjects: IStationSchemeObject[] = [];
    const alarmStatusObjects: IStationSchemeObject[] = [];
    const alarmStatusObjectsWithFault: IStationSchemeObject[] = [];
    const buttonObjectsWithFault: IStationSchemeObject[] = [];

    for (const obj of objects) {
      switch (obj.ObjectClass) {
        case ObjectClassParam.CAnalogDisplayObject:
        case ObjectClassParam.CThermometerObject:
        case ObjectClassParam.CAngularGaugeObject:
        case ObjectClassParam.CLinearGaugeObject:
        case ObjectClassParam.C7SegmentObject:
        case ObjectClassParam.CLedRoundObject:
        case ObjectClassParam.CLedRoundExtObject:
          foregroundControlObjects.push(obj);
          break;
        case ObjectClassParam.CSchemeButtonObject:
          if (isDefaultSchemeButtonCaption(obj)) {
            buttonObjects.push(obj);
          } else {
            styledButtonObjects.push(obj);
          }
          if (obj.unacknowledgedEvent === 2) {
            buttonObjectsWithFault.push(obj);
          }
          break;
        case ObjectClassParam.CAlarmStatusObject:
          if (isStationLabelObject(obj)) {
            foregroundControlObjects.push(obj);
          } else {
            alarmStatusObjects.push(obj);
            if (obj.unacknowledgedEvent === 2) {
              alarmStatusObjectsWithFault.push(obj);
            }
          }
          break;
      }
    }

    return {
      foregroundControlObjects,
      buttonObjects,
      styledButtonObjects,
      alarmStatusObjects,
      alarmStatusObjectsWithFault,
      buttonObjectsWithFault,
    };
  }, [objects]);

  const { height, width } = useImageSize({ imageData: scheme.PictureData });
  const natW = Math.max(1, width);
  const natH = Math.max(1, height);

  const animationKey = useMemo(() => `_${Date.now()}`, [flashUnacknowledgedAlarms, flashFaultAlarms]);

  const svgStyles = useMemo(() => makeSvgStyles(animationKey), [animationKey]);

  const dynamicStyles = useMemo(
    () =>
      objects
        .map(
          ({ ObjectClass, SchemeIdBindObject, left, top, right, bottom }) => `
          .${SCHEME_AREA_OVERLAY_CLASS} #object__${ObjectClass}__${SchemeIdBindObject}__${
            left + top + right + bottom
          } { cursor: pointer; }
          .${SCHEME_AREA_OVERLAY_CLASS} #object__${ObjectClass}__${SchemeIdBindObject}__${
            left + top + right + bottom
          } * { transition: opacity 0.2s ease-in-out, color 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out; }
        `,
        )
        .join(""),
    [objects],
  );

  const createClickHandler = useCallback(
    (SchemeIdBindObject: number, SchemeBindObjectType: number) => (event: MouseEvent) => {
      if (SchemeBindObjectType === 1) {
        selectStation(SchemeIdBindObject);
      } else if (SchemeBindObjectType === 2) {
        if (onShowDefects) {
          onShowDefects(SchemeIdBindObject);
        } else if (schemeInNewWindow) {
          openPopupWithParams(event, SchemeIdBindObject);
        } else {
          selectTrain(SchemeIdBindObject);
        }
      }
    },
    [schemeInNewWindow, selectStation, selectTrain, onShowDefects],
  );

  const overlayCommon = { flashUnacknowledgedAlarms, flashFaultAlarms };

  const renderStationControl = (obj: IStationSchemeObject, idx: number, options?: { styledSchemeButton?: boolean }) => {
    const box = stationObjectOverlayBox(obj, natW, natH);
    const ctrl = buildTrainControlFromStationObject(obj, idx, {
      dataFormat,
      styledSchemeButton: options?.styledSchemeButton,
      omitTrainAndTopLevelCaption: options?.styledSchemeButton,
    });
    const hasClickTarget = obj.SchemeIdBindObject != null && Number.isFinite(Number(obj.SchemeIdBindObject));
    const clickable =
      hasClickTarget && (obj.SchemeBindObjectType === 1 || obj.SchemeBindObjectType === 2)
        ? createClickHandler(Number(obj.SchemeIdBindObject), obj.SchemeBindObjectType)
        : undefined;
    const flashUnack = shouldFlashUnacknowledged(obj.unacknowledgedEvent, flashUnacknowledgedAlarms);

    const inner = (() => {
      switch (obj.ObjectClass) {
        case ObjectClassParam.CAnalogDisplayObject:
          return <TiAnalogDisplay control={ctrl} {...overlayCommon} />;
        case ObjectClassParam.CThermometerObject:
          return <TiThermometer control={ctrl} {...overlayCommon} />;
        case ObjectClassParam.CAngularGaugeObject:
          return <TiAngularGauge control={ctrl} {...overlayCommon} />;
        case ObjectClassParam.CLinearGaugeObject:
          return <TiLinearGauge control={ctrl} {...overlayCommon} />;
        case ObjectClassParam.C7SegmentObject:
          return <TiSevenSegmentAnalog control={ctrl} {...overlayCommon} />;
        case ObjectClassParam.CLedRoundObject:
        case ObjectClassParam.CLedRoundExtObject:
          return <TiLedRound control={ctrl} />;
        case ObjectClassParam.CAlarmStatusObject:
          return ctrl.componentName === "TiLabel" ? <TiLabel control={ctrl} {...overlayCommon} /> : null;
        case ObjectClassParam.CSchemeButtonObject:
          if (ctrl.componentName === "TiLabel") {
            return <TiLabel control={ctrl} {...overlayCommon} />;
          }
          return <TiSchemeButton control={ctrl} {...overlayCommon} />;
        default:
          return null;
      }
    })();

    if (!inner) return null;

    return (
      <Box
        key={getStationObjectKey(obj)}
        id={stationSchemeObjectDomId(obj)}
        className={`scheme-area-object${flashUnack ? " flashing-unacknowledged" : ""}`}
        sx={{
          position: "absolute",
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          pointerEvents: clickable ? "auto" : "none",
          cursor: clickable ? "pointer" : "default",
        }}
        onClick={clickable}
      >
        {inner}
      </Box>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%", display: "inline-block" }}>
      <Box
        className={SCHEME_AREA_OVERLAY_CLASS}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <style>{`${svgStyles}\n${dynamicStyles}`}</style>
        {buttonObjects.map((object, bi) => {
          const isStatusIndicator = schemeStatusIndicatorKeySet.has(getStationObjectKey(object));
          const resolvedAlarmStatus =
            isStatusIndicator && object.SchemeBindObjectType === 1
              ? object.alarmStatus ?? getStationAlarmStatus(object.SchemeIdBindObject) ?? 0
              : isStatusIndicator && object.SchemeBindObjectType === 2
                ? object.alarmStatus ?? getTrainAlarmStatus(object.SchemeIdBindObject) ?? 0
                : undefined;
          const ctrl = buildTrainControlFromStationObject(object, bi + foregroundControlObjects.length, {
            stationSchemeStatusIndicator: isStatusIndicator,
            resolvedAlarmStatus,
            omitTrainAndTopLevelCaption: true,
            dataFormat,
          });
          const box = stationObjectOverlayBox(object, natW, natH);
          const flashUnack = shouldFlashUnacknowledged(object.unacknowledgedEvent, flashUnacknowledgedAlarms);

          return (
            <Box
              key={getStationObjectKey(object)}
              id={stationSchemeObjectDomId(object)}
              className={`scheme-area-object${flashUnack ? " flashing-unacknowledged" : ""}`}
              onClick={createClickHandler(object.SchemeIdBindObject, object.SchemeBindObjectType)}
              sx={{
                position: "absolute",
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
                pointerEvents: "auto",
                cursor: "pointer",
              }}
            >
              <TiSchemeButton control={ctrl} {...overlayCommon} />
            </Box>
          );
        })}
        {alarmStatusObjects.map(object => {
          const { idleStatus, SchemeIdBindObject, SchemeBindObjectType, unacknowledgedEvent } = object;
          const alarmStatus =
            object.alarmStatus ??
            (SchemeBindObjectType === 1
              ? getStationAlarmStatus(SchemeIdBindObject)
              : getTrainAlarmStatus(SchemeIdBindObject));

          const box = stationObjectOverlayBox(object, natW, natH);
          const flashUnack = shouldFlashUnacknowledged(unacknowledgedEvent, flashUnacknowledgedAlarms);

          return (
            <Box
              key={getStationObjectKey(object)}
              id={stationSchemeObjectDomId(object)}
              className={`object${flashUnack ? " flashing-unacknowledged" : ""}`}
              onClick={createClickHandler(SchemeIdBindObject, SchemeBindObjectType)}
              sx={{
                position: "absolute",
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
                backgroundColor: resolveAlarmColor(alarmStatus ?? 0),
                opacity: idleStatus ? 0.5 : 1,
                cursor: "pointer",
                pointerEvents: "auto",
              }}
            />
          );
        })}
      </Box>
      <Box
        component="img"
        src={`data:image/jpeg;charset=utf-8;base64,${scheme.PictureData}`}
        alt=""
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: "auto",
          display: "block",
          verticalAlign: "top",
          pointerEvents: "none",
        }}
      />
      <Box
        className={SCHEME_AREA_OVERLAY_CLASS}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        {foregroundControlObjects.map((obj, i) => renderStationControl(obj, i))}
        {styledButtonObjects.map((obj, i) =>
          renderStationControl(obj, i + foregroundControlObjects.length, { styledSchemeButton: true }),
        )}
      </Box>
      {flashFaultAlarms ? (
        <Box
          className={SCHEME_AREA_OVERLAY_CLASS}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            zIndex: 3,
            pointerEvents: "none",
          }}
        >
          {alarmStatusObjectsWithFault.map(object => {
            const box = stationObjectOverlayBox(object, natW, natH);
            return (
              <Box
                key={`alarm_status_frame_${getStationObjectKey(object)}`}
                sx={{
                  position: "absolute",
                  left: box.left,
                  top: box.top,
                  width: box.width,
                  height: box.height,
                  boxSizing: "border-box",
                  border: "4px solid red",
                  pointerEvents: "none",
                }}
              />
            );
          })}
          {buttonObjectsWithFault.map(object => {
            const box = stationObjectOverlayBox(object, natW, natH);
            return (
              <Box
                key={`button_frame_${getStationObjectKey(object)}`}
                className="flashing-fault-rect"
                sx={{
                  position: "absolute",
                  left: box.left,
                  top: box.top,
                  width: box.width,
                  height: box.height,
                  boxSizing: "border-box",
                  border: "4px solid red",
                  pointerEvents: "none",
                }}
              />
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};
